import React from "react";
import { Autocomplete, Stack, TextField } from "@mui/material";
import colors from "../../Constants/colors";
import { User } from "../../Models/user";
import { Company } from "../../Models/company";
import { useTranslation } from "react-i18next";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

interface ActionLogFiltersProps {
  users: User[];
  selectedUser: User | null;
  handleUserChange: (
    event: React.SyntheticEvent,
    newValue: User | null,
  ) => void;
  onUserSearch: (value: string) => void;
  companies: Company[];
  selectedCompany: Company | null;
  handleCompanyChange: (
    event: React.SyntheticEvent,
    newValue: Company | null,
  ) => void;
  onCompanySearch: (value: string) => void;
  usersLoading?: boolean;
  companiesLoading?: boolean;
}

const ActionLogFilters: React.FC<ActionLogFiltersProps> = ({
  users,
  selectedUser,
  handleUserChange,
  onUserSearch,
  companies,
  selectedCompany,
  handleCompanyChange,
  onCompanySearch,
  usersLoading,
  companiesLoading,
}) => {
  const { t } = useTranslation("actionLog");
  const { colors: workspace } = useWorkspaceColors();

  const autocompleteStyle = {
    flex: 1,
    minWidth: 220,
  };

  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      fontSize: "0.875rem",
      borderRadius: 2,
      bgcolor: "white",
      "& fieldset": { borderColor: colors.gray300 },
      "&:hover fieldset": { border: `0.5px solid ${workspace.primary_dark}` },
      "&.Mui-focused fieldset": {
        border: `0.5px solid ${workspace.primary_dark}`,
        borderLeft: `4px solid ${workspace.primary_dark}`,
      },
    },
    "& .MuiInputLabel-root": {
      color: colors.gray600,
      fontSize: "0.875rem",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: workspace.primary_dark,
    },
    "& .MuiInputBase-input": {
      color: colors.primary_dark,
    },
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      sx={{ width: "100%" }}
    >
      <Autocomplete
        options={users}
        loading={usersLoading}
        getOptionLabel={(option) => `${option.name} ${option.surname}`}
        value={selectedUser}
        onChange={handleUserChange}
        size="small"
        onInputChange={(_event, newInputValue, reason) => {
          if (reason === "input") onUserSearch(newInputValue);
          if (reason === "clear") onUserSearch("");
        }}
        filterOptions={(options) => options}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={autocompleteStyle}
        componentsProps={{
          paper: {
            sx: {
              "& .MuiAutocomplete-option": {
                color: colors.primary_dark,
              },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("filters.user")}
            variant="outlined"
            sx={textFieldStyle}
            inputProps={{ ...params.inputProps, autoComplete: "off" }}
          />
        )}
      />

      <Autocomplete
        options={companies}
        loading={companiesLoading}
        getOptionLabel={(option) => option.name}
        value={selectedCompany}
        onChange={handleCompanyChange}
        size="small"
        onInputChange={(_event, newInputValue, reason) => {
          if (reason === "input") onCompanySearch(newInputValue);
          if (reason === "clear") onCompanySearch("");
        }}
        filterOptions={(options) => options}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={autocompleteStyle}
        componentsProps={{
          paper: {
            sx: {
              "& .MuiAutocomplete-option": {
                color: colors.primary_dark,
              },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("filters.company")}
            variant="outlined"
            sx={textFieldStyle}
            inputProps={{ ...params.inputProps, autoComplete: "off" }}
          />
        )}
      />
    </Stack>
  );
};

export default ActionLogFilters;
